/**
 * MetricPicker — choose an AI-visible metric column for a constraint / weight /
 * plot axis. A grouped <select> over the catalogue (categories as optgroups),
 * storing the column id and showing "Label (unit)". Native + accessible; no
 * dependency. Proprietary/hidden columns never appear (the backend already
 * excludes them from /metrics).
 */
import { useMemo } from 'preact/hooks'

export function MetricPicker({ value, onChange, metrics, ariaLabel, disabled }) {
  const groups = useMemo(() => {
    const byCat = new Map()
    for (const m of metrics || []) {
      if (!byCat.has(m.category)) byCat.set(m.category, [])
      byCat.get(m.category).push(m)
    }
    return [...byCat.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [metrics])

  // If the current value isn't in the catalogue (e.g. a hand-authored or
  // since-hidden column), keep it selectable so editing doesn't silently drop it.
  const known = useMemo(() => new Set((metrics || []).map((m) => m.column)), [metrics])
  const orphan = value && !known.has(value)

  return (
    <select
      className="admin-aim-input admin-flow-metric"
      value={value || ''}
      disabled={disabled}
      aria-label={ariaLabel || 'Metric'}
      onChange={(e) => onChange(e.currentTarget.value)}
    >
      <option value="">— select metric —</option>
      {orphan && <option value={value}>{value} (unknown)</option>}
      {groups.map(([cat, items]) => (
        <optgroup key={cat} label={cat}>
          {items.map((m) => (
            <option key={m.column} value={m.column}>
              {m.label}
              {m.unit ? ` (${m.unit})` : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}
